/**
 * The one way this app commits to something.
 *
 * Every button that changes a shared fact is held, not tapped, and none of them says so in words:
 * the fill crossing the button is the instruction, and the buzz at the end is the receipt. Labels
 * that read "Hold: Cancel" were the UI apologising for a gesture it had not made visible.
 *
 * Held rather than tapped because a phone is used walking onto a platform, through a coat pocket,
 * and one-handed in a car. A tap is what a brush against the screen looks like; a hold is not.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { postHaptic } from "../native";

/** Long enough that a brush cannot finish it, short enough not to feel like a punishment. */
export const HOLD_MS = 500;

/** Throwing work away, or interrupting the other person. Twice the thought, twice the hold. */
export const HOLD_DESTRUCTIVE_MS = 1000;

export function HoldButton({
	label,
	actionLabel,
	onConfirm,
	holdMs = HOLD_MS,
	className = "",
	phase,
	disabled = false,
}: {
	label: string;
	/** What the hold will do, for a screen reader that cannot see the fill. */
	actionLabel: string;
	onConfirm: () => void;
	holdMs?: number;
	className?: string;
	/** Colours the fill from the phase palette, the same way the progress line is coloured. */
	phase?: string;
	disabled?: boolean;
}) {
	const [holding, setHolding] = useState(false);
	const timerRef = useRef<number | null>(null);

	const release = useCallback(() => {
		if (timerRef.current != null) {
			window.clearTimeout(timerRef.current);
			timerRef.current = null;
		}
		setHolding(false);
	}, []);

	const begin = useCallback(() => {
		if (timerRef.current != null || disabled) {
			return;
		}
		setHolding(true);
		timerRef.current = window.setTimeout(() => {
			release();
			// Fired before the work, so the buzz lands with the fill rather than after a re-render.
			postHaptic("warning");
			onConfirm();
		}, holdMs);
	}, [disabled, holdMs, onConfirm, release]);

	useEffect(() => release, [release]);

	return (
		<button
			type="button"
			className={`holdbtn ${className} ${holding ? "is-holding" : ""}`}
			data-phase={phase}
			disabled={disabled}
			aria-label={`${actionLabel} — hold to confirm`}
			style={{ "--hold-ms": `${holdMs}ms` } as React.CSSProperties}
			onPointerDown={begin}
			onPointerUp={release}
			onPointerLeave={release}
			onPointerCancel={release}
			onKeyDown={(event) => {
				if (event.key === " " || event.key === "Enter") {
					event.preventDefault();
					begin();
				}
			}}
			onKeyUp={release}
			onBlur={release}
			// A long press on a control is iOS's cue to offer a share sheet; this one is the control.
			onContextMenu={(event) => event.preventDefault()}
		>
			<span className="holdbtn__fill" aria-hidden="true" />
			<span className="holdbtn__label">{label}</span>
		</button>
	);
}
